import { useState, useRef, useEffect, useCallback } from 'react';
import { usePageTitle } from '../../../hooks/usePageTitle';
import useFitRows from '../../../hooks/useFitRows';
import { useDrawerDrag } from '../../../hooks/useDrawerDrag';
import DashboardLayout from '../../../layouts/DashboardLayout/DashboardLayout';
import PageFooter from '../../../components/PageFooter/PageFooter';
import Button from '../../../components/Button/Button';
import Pagination from '../../../components/Pagination/Pagination';
import { X, Eye, EyeOff, Copy, Send, Upload, CircleCheck, ChevronDown, Plus } from 'lucide-react';
import MemberModal from './MemberModal';
import SearchInput from '../../../components/SearchInput/SearchInput';
import api from '../../../services/api';
import styles from './Team.module.css';

const EMPTY_FORM = {
  firstName: '', lastName: '', email: '', password: '',
  showPassword: false, accountType: 'Instant Funding DFY',
  access: '',
};
const EMPTY_BULK = { accountType: 'Instant Funding DFY', assetClass: '', paymentLink: '', file: null };

// Rows requested until the viewport can be measured. See useFitRows.
const TEAM_PAGE_SIZE = 12;
// How long a resize has to be quiet before the new row count reaches the API.
const RESIZE_SETTLE_MS = 300;

function generatePassword() {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
  return Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

/**
 * The value once it has stopped changing for `ms`.
 *
 * useFitRows already debounces its own measuring to one per frame, which is
 * right for a client-side slice but far too eager when each new count means a
 * request — a slow drag across the window edge would fire dozens.
 */
function useSettled(value, ms) {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

function Team() {
  usePageTitle('Team');
  const [members, setMembers]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [apiError, setApiError]         = useState('');
  const [search, setSearch]             = useState('');
  const [page, setPage]                 = useState(1);
  const [modal, setModal]               = useState(null);
  const [form, setForm]                 = useState(EMPTY_FORM);
  const [errors, setErrors]             = useState({});
  const [addedMember, setAddedMember]   = useState(null);
  const [showPwd, setShowPwd]           = useState(false);
  const [copied, setCopied]             = useState(false);
  const [bulkForm, setBulkForm]         = useState(EMPTY_BULK);
  const [bulkErrors, setBulkErrors]     = useState({});
  const [dragOver, setDragOver]         = useState(false);
  const [editMember, setEditMember]     = useState(null);
  const [editForm, setEditForm]         = useState({ firstName: '', lastName: '', email: '', access: '' });
  const [editErrors, setEditErrors]     = useState({});
  const [manageOpen, setManageOpen]     = useState(null);
  const [managePos, setManagePos]       = useState({ top: 0, right: 0 });
  const [submitLoading, setSubmitLoading]     = useState(false);
  const [sendingInvite, setSendingInvite]     = useState(false);
  const [inviteSent, setInviteSent]           = useState(false);
  const fileInputRef = useRef(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    if (!manageOpen) return;
    const handler = () => setManageOpen(null);
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [manageOpen]);

  const [totalPages, setTotalPages] = useState(1);

  const paginationRef = useRef(null);
  const footerRef     = useRef(null);
  const { bodyRef, rowsPerPage } = useFitRows({
    fallback: TEAM_PAGE_SIZE,
    belowRefs: [paginationRef, footerRef],
    min: 6,
    max: 30,
    gutter: 24, // .content bottom padding
  });

  /* Unlike the other tables, Team pages on the server: the row count is a
     request parameter, so every change to it costs a round trip. Wait for a
     resize to settle before acting on the new count. */
  const limit = useSettled(rowsPerPage, RESIZE_SETTLE_MS);

  /* Keep the reader's place when the count changes — page 3 of 12 starts at
     row 25, which is page 2 of 20. */
  const appliedLimit = useRef(limit);
  useEffect(() => {
    if (appliedLimit.current === limit) return;
    const firstRow = (page - 1) * appliedLimit.current;
    appliedLimit.current = limit;
    setPage(Math.floor(firstRow / limit) + 1);
  }, [limit, page]);

  const fetchMembers = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit });
    if (search) params.set('search', search);
    api.get(`/team?${params}`)
      .then(res => {
        setMembers(res.data.members.filter(m => m.id !== user.userId));
        setTotalPages(res.data.totalPages);
        // Fitting more rows per page can leave us past the end of the list.
        if (page > res.data.totalPages) setPage(Math.max(1, res.data.totalPages));
      })
      .catch(() => setApiError('Failed to load team members'))
      .finally(() => setLoading(false));
  }, [page, search, limit]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const currentPage = page;
  const pageRows    = members;

  function openAdd() { setForm({ ...EMPTY_FORM, password: generatePassword() }); setErrors({}); setInviteSent(false); setModal('add'); }
  function openBulk() { setBulkForm(EMPTY_BULK); setBulkErrors({}); setModal('bulk'); }
  function closeModal() { setModal(null); setSubmitLoading(false); }
  const { modalRef: teamDrawerRef, dragHandleProps: teamDragHandle } = useDrawerDrag(closeModal);

  function openRemove(member) { setEditMember(member); setModal('remove'); }

  function openEdit(member) {
    setEditMember(member);
    setEditForm({ firstName: member.firstName, lastName: member.lastName, email: member.email, access: member.access.toLowerCase() });
    setEditErrors({});
    setModal('edit');
  }

  function setEditField(k, v) {
    setEditForm(f => ({ ...f, [k]: v }));
    if (editErrors[k]) setEditErrors(e => ({ ...e, [k]: '' }));
  }

  async function submitEdit() {
    const e = {};
    if (!editForm.firstName.trim()) e.firstName = 'First name required';
    if (!editForm.lastName.trim())  e.lastName  = 'Last name required';
    if (!editForm.email.trim())     e.email     = 'Email required';
    if (!editForm.access)           e.access    = 'Member access required';
    if (Object.keys(e).length) { setEditErrors(e); return; }

    setSubmitLoading(true);
    try {
      await api.put(`/team/${editMember.id}`, editForm);
      closeModal();
      fetchMembers();
    } catch (err) {
      setEditErrors({ general: err.response?.data?.error || 'Failed to update member' });
    } finally {
      setSubmitLoading(false);
    }
  }

  function setField(k, v) {
    setForm(f => ({ ...f, [k]: v }));
    if (errors[k]) setErrors(e => ({ ...e, [k]: '' }));
  }

  function validateAdd() {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'First name required';
    if (!form.lastName.trim())  e.lastName  = 'Last name required';
    if (!form.email.trim())     e.email     = 'Email required';
    if (!form.password.trim())  e.password  = 'Password required';
    if (!form.access)           e.access    = 'Member access required';
    return e;
  }

  async function submitAdd() {
    const e = validateAdd();
    if (Object.keys(e).length) { setErrors(e); return; }

    setSubmitLoading(true);
    try {
      const { data } = await api.post('/team', {
        firstName:   form.firstName,
        lastName:    form.lastName,
        email:       form.email,
        password:    form.password,
        access:      form.access,
        companyId:   user.companyId,
      });
      setAddedMember({ ...form, fullName: `${form.firstName} ${form.lastName}` });
      fetchMembers();
      setShowPwd(false);
      setCopied(false);
      setModal('added');
    } catch (err) {
      setErrors({ general: err.response?.data?.error || 'Failed to add member' });
    } finally {
      setSubmitLoading(false);
    }
  }

  function copyPassword() {
    navigator.clipboard.writeText(addedMember.password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function sendInstructions() {
    if (sendingInvite || inviteSent) return;
    setSendingInvite(true);
    try {
      await api.post('/team/resend-invite', { email: addedMember.email, password: addedMember.password });
      setInviteSent(true);
    } catch {
      // silently fail — user can retry
    } finally {
      setSendingInvite(false);
    }
  }

  function setBulkField(k, v) {
    setBulkForm(f => ({ ...f, [k]: v }));
    if (bulkErrors[k]) setBulkErrors(e => ({ ...e, [k]: '' }));
  }

  function validateBulk() {
    const e = {};
    if (!bulkForm.assetClass)         e.assetClass  = 'Asset class required';
    if (!bulkForm.paymentLink.trim()) e.paymentLink = 'Payment link required';
    if (!bulkForm.file)               e.file        = 'File upload required';
    return e;
  }

  async function submitBulk() {
    const e = validateBulk();
    if (Object.keys(e).length) { setBulkErrors(e); return; }

    setSubmitLoading(true);
    try {
      const formData = new FormData();
      formData.append('file',        bulkForm.file);
      formData.append('accountType', bulkForm.accountType);
      formData.append('assetClass',  bulkForm.assetClass);
      formData.append('paymentLink', bulkForm.paymentLink);

      await api.post('/team/bulk', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      closeModal();
      fetchMembers();
    } catch (err) {
      setBulkErrors({ general: err.response?.data?.error || 'Failed to process bulk request' });
    } finally {
      setSubmitLoading(false);
    }
  }

  async function confirmRemove() {
    setSubmitLoading(true);
    try {
      await api.delete(`/team/${editMember.id}`);
      fetchMembers();
      closeModal();
    } catch (err) {
      setApiError(err.response?.data?.error || 'Failed to remove member');
      closeModal();
    } finally {
      setSubmitLoading(false);
    }
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) setBulkField('file', file);
  }

  const capitalize = s => s.charAt(0).toUpperCase() + s.slice(1);

  return (
    <DashboardLayout>
      <div className={styles.teamPage}>

        <div className={styles.header}>
          <h1 className={styles.title}>Team</h1>
          <div className={styles.headerActions}>
            <Button variant="secondary" size="sm" fullWidth={false} onClick={openBulk}>Bulk request</Button>
            <Button variant="primary" size="sm" fullWidth={false} onClick={openAdd}><Plus size={16} /> Add member</Button>
          </div>
        </div>

        <SearchInput
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search member"
          className={styles.searchWrap}
        />

        {apiError && <p style={{ color: 'red', marginBottom: 12 }}>{apiError}</p>}

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>Member</th>
                <th className={styles.th}>Access</th>
                <th className={`${styles.th} ${styles.emailCol}`}>Email</th>
                <th className={`${styles.th} ${styles.thRight}`}>Action</th>
              </tr>
            </thead>
            <tbody ref={bodyRef}>
              {loading ? (
                <tr><td colSpan={4} className={styles.emptyState}>Loading…</td></tr>
              ) : members.length === 0 ? (
                <tr>
                  <td colSpan={4} className={styles.emptyState}>
                    {search ? `No results found for "${search}"` : 'No team members yet'}
                  </td>
                </tr>
              ) : pageRows.map(m => (
                <tr key={m.id} className={styles.row}>
                  <td className={styles.td}>
                    <div className={styles.memberCell}>
                      <span className={`${styles.dot} ${m.active ? styles.dotActive : styles.dotInactive}`} />
                      {m.name}
                    </div>
                  </td>
                  <td className={styles.td}>{m.access}</td>
                  <td className={`${styles.td} ${styles.emailCol}`}>{m.email}</td>
                  <td className={`${styles.td} ${styles.tdRight}`}>
                    <div className={styles.manageWrap} onMouseDown={e => e.stopPropagation()}>
                      <button
                        className={styles.manageBtn}
                        onClick={e => {
                          if (manageOpen === m.id) { setManageOpen(null); return; }
                          const rect = e.currentTarget.getBoundingClientRect();
                          setManagePos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                          setManageOpen(m.id);
                        }}
                      >
                        Manage
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div ref={paginationRef}>
          <Pagination page={currentPage} totalPages={totalPages} onPageChange={setPage} />
        </div>

        <PageFooter ref={footerRef} />

        {/* ── Manage dropdown (fixed to escape overflow:hidden) ── */}
        {manageOpen && pageRows.find(m => m.id === manageOpen) && (() => {
          const m = pageRows.find(r => r.id === manageOpen);
          return (
            <div
              className={styles.manageDropdown}
              style={{ position: 'fixed', top: managePos.top, right: managePos.right, zIndex: 200 }}
              onMouseDown={e => e.stopPropagation()}
            >
              <button className={styles.manageDropdownItem} onClick={() => { setManageOpen(null); openEdit(m); }}>Edit</button>
              <button className={styles.manageDropdownItem} onClick={() => { setManageOpen(null); openRemove(m); }}>Remove</button>
            </div>
          );
        })()}

        {/* ── Add Member Modal ── */}
        {modal === 'add' && (
          <MemberModal
            mode="add"
            form={form}
            errors={errors}
            loading={submitLoading}
            onClose={closeModal}
            onSetField={setField}
            onToggleAccess={v => setField('access', v)}
            onSubmit={submitAdd}
          />
        )}

        {/* ── Member Added Modal ── */}
        {modal === 'added' && addedMember && (
          <div className={styles.overlay} onClick={closeModal}>
            <div className={styles.modal} ref={teamDrawerRef} onClick={e => e.stopPropagation()}>
              <div className={styles.pullHandle} {...teamDragHandle} />
              <div className={styles.modalHeader}>
                <h2 className={styles.modalTitle}>Member added</h2>
                <button className={styles.closeBtn} onClick={closeModal}><X size={20} /></button>
              </div>
              <div className={styles.modalBody}>
                <div className={styles.successBanner}>
                  <CircleCheck size={20} />
                  {addedMember.fullName} has been added successfully
                </div>
                <p className={styles.sectionLabel}>DETAILS</p>
                <div className={styles.detailsList}>
                  {[
                    ['Full name',    addedMember.fullName],
                    ['Email',        addedMember.email],
                    ['Password',     null],
                    // ['Account type', addedMember.accountType], // hidden for now
                    ['Access',       capitalize(addedMember.access)],
                  ].map(([label, value]) => (
                    <div key={label} className={styles.detailRow}>
                      <span className={styles.detailLabel}>{label}</span>
                      {label === 'Password' ? (
                        <div className={styles.detailPwdWrap}>
                          <span className={styles.detailValue}>
                            {showPwd ? addedMember.password : '•'.repeat(Math.min(addedMember.password.length, 12))}
                          </span>
                          <button className={styles.iconBtn} onClick={() => setShowPwd(v => !v)}>
                            {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                          </button>
                          <button className={styles.iconBtn} onClick={copyPassword} title={copied ? 'Copied!' : 'Copy'}>
                            <Copy size={15} style={{ color: copied ? 'var(--green-400)' : undefined }} />
                          </button>
                        </div>
                      ) : (
                        <span className={styles.detailValue}>{value}</span>
                      )}
                    </div>
                  ))}
                </div>
                <div className={styles.divider} />
                <p className={styles.sectionLabel}>SIGN-IN INSTRUCTIONS</p>
                <div className={styles.signInSection}>
                  <p className={styles.signInText}>
                    An email will be sent to <strong>{addedMember.email}</strong> with their login details and a link to get started.
                  </p>
                  <button className={styles.sendBtn} onClick={sendInstructions} disabled={sendingInvite || inviteSent}>
                    {inviteSent ? 'Sent!' : sendingInvite ? 'Sending…' : <> Send instructions <Send size={14} /></>}
                  </button>
                </div>
              </div>
              <div className={`${styles.modalFooter} ${styles.addedFooter}`}>
                <Button variant="secondary" size="sm" fullWidth={false} onClick={openAdd}>Add another member</Button>
                <Button variant="ghost" size="sm" fullWidth={false} onClick={closeModal}>Done</Button>
              </div>
            </div>
          </div>
        )}

        {/* ── Edit Member Modal ── */}
        {modal === 'edit' && editMember && (
          <MemberModal
            mode="edit"
            form={editForm}
            errors={editErrors}
            loading={submitLoading}
            onClose={closeModal}
            onSetField={setEditField}
            onToggleAccess={v => setEditField('access', v)}
            onSubmit={submitEdit}
          />
        )}

        {/* ── Remove Member Modal ── */}
        {modal === 'remove' && editMember && (
          <div className={styles.overlay} onClick={closeModal}>
            <div className={styles.modal} ref={teamDrawerRef} onClick={e => e.stopPropagation()}>
              <div className={styles.pullHandle} {...teamDragHandle} />
              <div className={styles.modalHeader}>
                <h2 className={styles.modalTitle}>Remove member?</h2>
                <button className={styles.closeBtn} onClick={closeModal}><X size={20} /></button>
              </div>
              <div className={styles.modalBody}>
                <p className={styles.removeText}>Removing a team member can't be undone.</p>
                <p className={styles.removeText}>
                  By proceeding, {editMember.name.split(' ')[0]} will lose access to the POW portal.
                </p>
              </div>
              <div className={`${styles.modalFooter} ${styles.removeModalFooter}`}>
                <Button variant="secondary" size="sm" fullWidth={false} onClick={closeModal}>Cancel</Button>
                <Button variant="ghost" size="sm" fullWidth={false} className={styles.removeBtn} onClick={confirmRemove} disabled={submitLoading}>
                  {submitLoading ? 'Removing…' : 'Remove'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ── Bulk Request Modal ── */}
        {modal === 'bulk' && (
          <div className={styles.overlay} onClick={closeModal}>
            <div className={styles.modal} ref={teamDrawerRef} onClick={e => e.stopPropagation()}>
              <div className={styles.pullHandle} {...teamDragHandle} />
              <div className={styles.modalHeader}>
                <h2 className={styles.modalTitle}>Bulk request</h2>
                <button className={styles.closeBtn} onClick={closeModal}><X size={20} /></button>
              </div>
              <div className={styles.modalBody}>
                {/* <p className={styles.sectionLabel}>ACCOUNT DETAILS</p> */}
                {/* <div className={styles.formGroup}>
                  <label className={styles.fieldLabel}>Account type</label>
                  <div className={styles.selectWrap}>
                    <select className={styles.fieldInput} value="Instant Funding DFY" disabled style={{ opacity: 0.6, cursor: 'default', pointerEvents: 'none' }}>
                      <option>Instant Funding DFY</option>
                    </select>
                    <ChevronDown size={14} className={styles.selectChevron} style={{ opacity: 0.4 }} />
                  </div>
                </div> */}
                <p className={styles.sectionLabel}>ASSET CLASS</p>
                <div className={styles.formGroup}>
                  <div className={styles.radioGroup}>
                    {['Forex', 'Futures'].map(opt => (
                      <label key={opt} className={styles.radioItem}>
                        <input type="radio" name="bulkAssetClass" checked={bulkForm.assetClass === opt.toLowerCase()} onChange={() => setBulkField('assetClass', opt.toLowerCase())} />
                        {opt}
                      </label>
                    ))}
                  </div>
                  {bulkErrors.assetClass && <p className={styles.fieldError}>{bulkErrors.assetClass}</p>}
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.fieldLabel}>Payment link</label>
                  <input type="url" placeholder="Paste link here" className={`${styles.fieldInput} ${bulkErrors.paymentLink ? styles.fieldInputError : ''}`} value={bulkForm.paymentLink} onChange={e => setBulkField('paymentLink', e.target.value)} />
                  {bulkErrors.paymentLink && <p className={styles.fieldError}>{bulkErrors.paymentLink}</p>}
                </div>
                <p className={styles.sectionLabel}>FILE UPLOAD</p>
                <div className={`${styles.uploadArea} ${dragOver ? styles.uploadAreaDragOver : ''}`} onClick={() => fileInputRef.current?.click()} onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={handleDrop}>
                  <Upload size={28} className={styles.uploadIcon} />
                  <p className={styles.uploadText}>
                    {bulkForm.file ? bulkForm.file.name : (
                      <><span className={styles.uploadTextDesktop}>Upload or drag file</span><span className={styles.uploadTextMobile}>Upload file</span></>
                    )}
                  </p>
                  <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) setBulkField('file', e.target.files[0]); }} />
                </div>
                {bulkErrors.file && <p className={styles.fieldError}>{bulkErrors.file}</p>}
              </div>
              {bulkErrors.general && <p className={styles.generalError}>{bulkErrors.general}</p>}
              <div className={`${styles.modalFooter} ${styles.bulkFooter}`}>
                <Button variant="secondary" size="sm" fullWidth={false} onClick={closeModal}>Cancel</Button>
                <Button variant="primary" size="sm" fullWidth={false} onClick={submitBulk} disabled={submitLoading}>
                  {submitLoading ? 'Submitting…' : 'Submit'}
                </Button>
              </div>
            </div>
          </div>
        )}

      </div>
    </DashboardLayout>
  );
}

export default Team;
